import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export enum PopupStyle {
  ACKNOWLEDGMENT = 'acknowledgment',  // Single button - stores read flag
  NAVIGATION = 'navigation'            // Dual buttons - always shows, no read flag
}

export interface NewsPopupConfig {
  effectiveDate: Date;
  expiryDate: Date;
  popupId: string; // Unique identifier for the popup message
  style: PopupStyle; // Style of the popup
  leftButtonText?: string; // Text for left button (navigation style only)
  leftButtonUrl?: string; // URL for left button (navigation style only)
  rightButtonText?: string; // Text for right button (navigation style only)
}

@Injectable({
  providedIn: 'root'
})
export class NewsPopupService {
  private readonly STORAGE_KEY_PREFIX = 'news_popup_read_';

  // Configuration for the popup - loaded from environment
  private config: NewsPopupConfig = {
    effectiveDate: new Date(environment.newsPopup.effectiveDate),
    expiryDate: new Date(environment.newsPopup.expiryDate),
    popupId: environment.newsPopup.popupId,
    style: environment.newsPopup.style === 'navigation' ? PopupStyle.NAVIGATION : PopupStyle.ACKNOWLEDGMENT,
    leftButtonUrl: environment.newsPopup.leftButtonUrl,
    leftButtonText: environment.newsPopup.leftButtonText,
    rightButtonText: environment.newsPopup.rightButtonText
  };

  constructor() {}

  /**
   * Update the popup configuration
   */
  setConfig(config: NewsPopupConfig): void {
    this.config = config;
  }

  /**
   * Get the current popup configuration
   */
  getConfig(): NewsPopupConfig {
    return this.config;
  }

  /**
   * Get the storage key for the current popup
   */
  getStorageKey(): string {
    if (!this.config.popupId) {
      throw new Error('Popup ID is not defined in the configuration.');
    }
    return this.STORAGE_KEY_PREFIX + this.config.popupId;
  }


  /**
   * Check if the popup should be displayed
   * Returns true if:
   * 1. Current date is within effective and expiry dates
   * 2. For ACKNOWLEDGMENT style: User has not acknowledged this popup before
   * 3. For NAVIGATION style: Always show (ignores read flag)
   */
  shouldShowPopup(): boolean {
    const now = new Date();
    const effectiveDate = new Date(this.config.effectiveDate);
    const expiryDate = new Date(this.config.expiryDate);

    // Check if current date is within the valid range
    const isWithinDateRange = now >= effectiveDate && now <= expiryDate;

    if (!isWithinDateRange) {
      return false;
    }

    // For NAVIGATION style, always show the popup
    if (this.config.style === PopupStyle.NAVIGATION) {
      return true;
    }

    // For ACKNOWLEDGMENT style, check if user has already acknowledged
    const hasRead = this.hasUserReadPopup();
    return !hasRead;
  }

  /**
   * Check if user has read this specific popup
   */
  private hasUserReadPopup(): boolean {
    const storageKey = this.getStorageKey();
    const readFlag = localStorage.getItem(storageKey);
    console.log('Checking localStorage for key:', storageKey, 'Value:', readFlag);
    return readFlag === 'true';
  }

  /**
   * Mark the popup as read by the user
   */
  markAsRead(): void {
    const storageKey = this.getStorageKey();
    console.log('Attempting to set localStorage key:', storageKey);
    try {
      localStorage.setItem(storageKey, 'true');
      console.log('Successfully set localStorage. Verification:', localStorage.getItem(storageKey));
    } catch (error) {
      console.error('Error setting localStorage:', error);
    }
  }

  /**
   * Clear the read flag for the current popup (useful for testing)
   */
  clearReadFlag(): void {
    const storageKey = this.getStorageKey();
    localStorage.removeItem(storageKey);
  }

  /**
   * Clear all read flags for all popups
   */
  clearAllReadFlags(): void {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(this.STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  }
}
